import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { hasParticleEmbeddedImageHint } from '../../features/ttqib/embeddedImageUtils';
import { TtqibEmbeddedImage } from '../../features/ttqib/TtqibEmbeddedImage';
import { useParticleEmbeddedImage } from '../../features/ttqib/useParticleEmbeddedImage';

type Props = {
  particleId: string;
  metadata?: unknown;
  /** 列表滚动容器；不传则相对视口观察 */
  root?: Element | null;
};

/**
 * 添加颗粒列表：有嵌入图时，进入可视区域后再解析并渲染缩略图。
 */
export function TtqPickerParticleEmbeddedThumb({ particleId, metadata, root }: Props) {
  const hint = hasParticleEmbeddedImageHint(metadata);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!hint) return;
    const node = hostRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
        }
      },
      { root: root || null, rootMargin: '120px', threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hint, root, particleId]);

  const { data, loading } = useParticleEmbeddedImage(particleId, hint && visible);
  const crop = data?.crop;

  if (!hint) return null;

  if (!crop) {
    if (!visible || !loading) {
      return <div ref={hostRef} className="mt-2" aria-hidden />;
    }
    return (
      <div
        ref={hostRef}
        className="mt-2 flex h-16 w-28 items-center justify-center rounded-md border border-border bg-canvas/70"
      >
        <Loader2 size={14} className="animate-spin text-muted" />
      </div>
    );
  }

  return (
    <div ref={hostRef} className="mt-2 max-w-[10.5rem]">
      <TtqibEmbeddedImage
        record={crop}
        fullSourceUrl={data?.fullSourceUrl}
        ossId={data?.ossId}
        height={72}
        width={168}
        zoomable={false}
      />
    </div>
  );
}
